/**
 * Library page — mobile "Filters & Sort" bottom sheet.
 *
 * Wraps the SAME five <select data-tayab-lit-filter="..."> elements
 * tayab-lit-listing.js already listens on (via event delegation on the
 * outer [data-tayab-lit-listing] container) — nothing here duplicates
 * those controls or their change handling. This element only toggles
 * whether .tayab-lit-filter-dropdowns is presented inline (desktop) or
 * as a mobile bottom sheet (see the max-width: 749px block in
 * tayab-custom.css), plus the sheet's own open/close mechanics:
 * overlay-click-to-close, Escape-to-close, body scroll lock, and focus
 * trapping while open.
 *
 * Desktop is handled by genuinely setting the <details> element's
 * `open` attribute via matchMedia, not by CSS-faking it open (e.g.
 * `display: contents` on the <details> itself) — tried that first and
 * it silently failed: `display: contents` on a <details>/<summary>
 * pair is inconsistently supported (several browsers keep applying the
 * native "hide non-summary children while closed" behavior underneath
 * it regardless), confirmed live as .tayab-lit-filter-dropdowns
 * reporting correct computed styles/geometry while still not actually
 * painting. Real open/closed state, native to the element, avoids that
 * class of bug entirely.
 *
 * Deliberately its own small custom element rather than reusing
 * details-modal.js: that component's open() hard-codes focusing
 * `this.detailsContainer.querySelector('input:not([type="hidden"])')`
 * (built for the search modal's text input) and calls .focus() on the
 * result unconditionally — with no <input> in this sheet (only
 * <select> elements) that query returns null and throws. Focusing the
 * close button instead sidesteps that assumption entirely.
 */
class TayabLitFilterModal extends HTMLElement {
  constructor() {
    super();
    this.detailsEl = this.querySelector('details');
    this.summaryEl = this.querySelector('summary');
    this.panelEl = this.querySelector('[data-tayab-lit-filter-modal-panel]');
    this.closeButtons = this.querySelectorAll('[data-tayab-lit-filter-modal-close]');
    this.overlayEl = this.querySelector('[data-tayab-lit-filter-modal-overlay]');
    this.desktopQuery = window.matchMedia('(min-width: 750px)');

    this.summaryEl.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close.bind(this)));
    this.overlayEl.addEventListener('click', this.close.bind(this));
    this.detailsEl.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    });
    this.desktopQuery.addEventListener('change', this.syncDesktopState.bind(this));

    this.syncDesktopState();
  }

  isDesktop() {
    return this.desktopQuery.matches;
  }

  isOpen() {
    return this.detailsEl.hasAttribute('open');
  }

  syncDesktopState() {
    this.isDesktop() ? this.open() : this.close();
  }

  onSummaryClick(event) {
    // Only the mobile sheet trigger calls this — at 750px+ the summary
    // itself is hidden (see CSS), so this never fires there in
    // practice; the isDesktop() guard is just defensive.
    event.preventDefault();
    if (this.isDesktop()) return;
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    this.detailsEl.setAttribute('open', '');

    if (this.isDesktop()) {
      // .tayab-lit-filter-dropdowns renders inline there — no sheet
      // chrome (overlay/scroll-lock/focus-trap) applies, and any left
      // over from a mobile-open state before a resize needs clearing.
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus();
      return;
    }

    document.body.classList.add('overflow-hidden');
    trapFocus(this.panelEl, this.closeButtons[0]);
  }

  close() {
    if (this.isDesktop()) return; // stays open there regardless
    if (!this.isOpen()) return;

    removeTrapFocus(this.summaryEl);
    this.detailsEl.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('tayab-lit-filter-modal', TayabLitFilterModal);
